import { RootState } from "core-integration/src/store";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import {
  IElement,
  IElementEventStats,
} from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import {
  getElementEventOpponents,
  getFixturesForEventByTeam,
} from "core-integration/src/store/fixtures/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { InfoText } from "plos/src/components/tooltips/InfoText";
import { getDifficultyColor } from "plos/src/styles/difficultyColors";
import {
  listTableCell,
  listTableFixtureColumn,
  listTableStatColumn,
  listTableWideColumn,
  priceChangeCell,
} from "plos/src/styles/listTables.css";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { getAccessibleFixtureLabels } from "plos/src/utils/fixtures";
import { integerToMoneyWithCurrency } from "plos/src/utils/money";
import { Fragment, ReactNode } from "react";
import { useSelector } from "react-redux";
import {
  IOverlayColumn,
  OverlayColumnVariant,
} from "../../hooks/useOverlayColumns";
import { ownershipDirectionWrapper } from "../PitchControls/ElementOverlayValue/elementOverlayValue.css";
import { OwnershipIcon } from "../PitchControls/ElementOverlayValue/OwnershipIcon";
import { SelectedOverlay } from "../PitchControls/types";
import { OwnershipCell } from "../PriceChanges/OwnershipCell";
import { PriceChangeStatus } from "../PriceChanges/PriceChangeStatus";
import {
  getPriceChangeStatus,
  priceChangePredictionEnabled,
} from "../PriceChanges/utils";
import ListViewFDRCells from "./ListViewFDRCells";

// Fixtures need a wider column, used in fixture column and FDR columns
const headerClassByVariant: Record<OverlayColumnVariant, string> = {
  small: listTableStatColumn,
  medium: listTableFixtureColumn,
  wide: listTableWideColumn,
};

export const OverlayHeaderCells = ({
  columns,
}: {
  columns: IOverlayColumn[];
}) => (
  <>
    {columns.map((col) => (
      <th
        key={col.key}
        scope="col"
        className={headerClassByVariant[col.variant]}
      >
        {col.label ? (
          <InfoText label={col.label}>{col.shortName}</InfoText>
        ) : (
          col.shortName
        )}
      </th>
    ))}
  </>
);

export interface OverlayPickPrices {
  selling_price: number;
  purchase_price: number;
}

interface OverlayCellsProps {
  overlay: SelectedOverlay;
  element: IElement;
  stats: ReadonlyArray<IElementStat>;
  pick?: { multiplier: number };
  data?: IElementEventStats | IElement;
  pickPrices?: OverlayPickPrices | null;
  currencyDivisor?: number;
}

/**
 * Renders the list view data cells for the active overlay.
 */
const OverlayCells = ({
  overlay,
  element,
  stats,
  pick,
  data,
  pickPrices,
  currencyDivisor = 10,
}: OverlayCellsProps) => {
  const teamsById = useSelector(getTeamsById);
  const nextEvent = useSelector(getNextEvent);
  const fixturesForNextEventByTeam = useSelector((state: RootState) =>
    nextEvent ? getFixturesForEventByTeam(state, nextEvent.id) : null,
  );
  const fixtures = fixturesForNextEventByTeam
    ? fixturesForNextEventByTeam[element.team]
    : [];
  const elementEventOpponents = useSelector((state: RootState) =>
    fixtures.length ? getElementEventOpponents(state, element, fixtures) : [],
  );
  const accessibleFixtureLabels = getAccessibleFixtureLabels(
    element,
    fixtures,
    teamsById,
  );

  const gwPoints = data
    ? data.total_points * Math.max(pick?.multiplier ?? 1, 1)
    : element.event_points;

  const predictedProgress =
    element.price_change_projections?.[0]?.projected_percent;

  const statValue = (name: string): ReactNode =>
    (data ? data[name] : element[name]) as ReactNode;

  const formCell = (
    <td className={listTableCell} key="form">
      {element.form}
    </td>
  );
  const gwPointsCell = (
    <td className={listTableCell} key="points">
      {gwPoints}
    </td>
  );
  const totalPointsCell = (
    <td className={listTableCell} key="total_points">
      {element.total_points}
    </td>
  );
  const currentPriceCell = (
    <td className={listTableCell} key="now_cost">
      {integerToMoneyWithCurrency(element.now_cost, currencyDivisor)}
    </td>
  );
  const purchasePriceCell = (
    <td className={listTableCell} key="purchase_price">
      {pickPrices
        ? integerToMoneyWithCurrency(pickPrices.purchase_price, currencyDivisor)
        : "-"}
    </td>
  );
  const sellingPriceCell = (
    <td className={listTableCell} key="selling_price">
      {pickPrices
        ? integerToMoneyWithCurrency(pickPrices.selling_price, currencyDivisor)
        : "-"}
    </td>
  );
  const fixtureCell = (
    <td className={listTableCell} key="fixture">
      {elementEventOpponents.length ? (
        elementEventOpponents.map((opp, index) => (
          <Fragment key={`${opp}-${index}`}>
            <div aria-hidden="true">{opp}</div>
            <div className={visuallyHidden}>
              {accessibleFixtureLabels[index]}
            </div>
          </Fragment>
        ))
      ) : (
        <>-</>
      )}
    </td>
  );
  const statusCell = (
    <td className={listTableCell} key="status">
      <PriceChangeStatus element={element} />
    </td>
  );
  const progressCell = (
    <td key="progress">
      {(() => {
        // Single text node so screen readers announce it consistently
        const progressLabel = `${
          Number(element.price_change_percent) > 0 ? "+" : ""
        }${element.price_change_percent}%`;
        return (
          <span
            className={`${priceChangeCell} ${getDifficultyColor(
              getPriceChangeStatus(element),
            )}`}
            aria-label={progressLabel}
          >
            {progressLabel}
          </span>
        );
      })()}
    </td>
  );
  const predictedProgressCell = priceChangePredictionEnabled ? (
    <td key="predicted_progress">
      <span
        className={`${priceChangeCell} ${getDifficultyColor(
          getPriceChangeStatus(element),
        )}`}
      >
        {Number(predictedProgress) > 0
          ? `+${predictedProgress}`
          : predictedProgress}
        %
      </span>
    </td>
  ) : null;
  const ownershipTrendCell = (
    <td className={listTableCell} key="ownership_trend">
      <OwnershipCell element={element} />
    </td>
  );

  switch (overlay) {
    case "opponent":
    case "form":
      return (
        <>
          {formCell}
          {gwPointsCell}
          {totalPointsCell}
          {fixtureCell}
        </>
      );
    case "currentPrice":
    case "sellingPrice":
      return (
        <>
          {currentPriceCell}
          {purchasePriceCell}
          {sellingPriceCell}
          {formCell}
          {totalPointsCell}
          {fixtureCell}
        </>
      );
    case "fdr":
      return <ListViewFDRCells element={element} />;
    case "ownership":
      return (
        <td className={listTableCell}>
          <div className={ownershipDirectionWrapper}>
            {element.selected_by_percent}%
            <OwnershipIcon
              transfersIn={element.transfers_in_event}
              transfersOut={element.transfers_out_event}
            />
          </div>
        </td>
      );
    case "points":
      return (
        <>
          {gwPointsCell}
          {stats.map((stat) => (
            <td className={listTableCell} key={stat.name}>
              {statValue(stat.name)}
            </td>
          ))}
        </>
      );
    case "price_change":
      return (
        <>
          {statusCell}
          {progressCell}
          {predictedProgressCell}
          {ownershipTrendCell}
          {currentPriceCell}
          {purchasePriceCell}
          {sellingPriceCell}
        </>
      );
    default:
      return null;
  }
};

export default OverlayCells;
